import { useEffect, useState, useMemo } from "react";
import { Bell, Send, Loader2, Users, Megaphone, Trash2, Download, Search, CheckCircle2, Clock, CalendarClock, Repeat, CalendarDays, Power, ChevronDown, Pencil, Save, X, UserPlus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SIMPLE_LABELS = { all: "All updates", tournaments: "Tournaments", promos: "News/Promos", videos: "Live streams", merch: "Merch" };
const SIMPLE_CATS = ["all", "tournaments", "promos", "videos", "merch"];
const DOW = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const fmtEastern = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: "America/Toronto", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    }) + " ET";
  } catch {
    return iso;
  }
};

export function NotificationsManager() {
  const [stats, setStats] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [divIds, setDivIds] = useState([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [subsOpen, setSubsOpen] = useState(true);
  const [editCatId, setEditCatId] = useState(null);
  const [catDraft, setCatDraft] = useState([]);
  const [savingCat, setSavingCat] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addCats, setAddCats] = useState(["all"]);
  const [addWelcome, setAddWelcome] = useState(false);
  const [addBusy, setAddBusy] = useState(false);

  const toggleDiv = (id) => setDivIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // Scheduling
  const [schedules, setSchedules] = useState([]);
  const [schedKind, setSchedKind] = useState("once");
  const [schedDivId, setSchedDivId] = useState("");
  const [schedMsg, setSchedMsg] = useState("");
  const [runDate, setRunDate] = useState("");
  const [runTime, setRunTime] = useState("20:00");
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [weeklyTime, setWeeklyTime] = useState("20:00");
  const [endDate, setEndDate] = useState("");
  const [schedBusy, setSchedBusy] = useState(false);

  const loadSchedules = () => api.get("/admin/scheduled-notifications").then((r) => setSchedules(r.data.scheduled || [])).catch(() => {});

  useEffect(() => {
    api.get("/admin/subscribers").then((r) => setStats(r.data)).catch(() => {});
    api.get("/divisions").then((r) => setDivisions((r.data || []).filter((d) => !d.archived))).catch(() => {});
    loadSchedules();
  }, []);

  const reload = () => api.get("/admin/subscribers").then((r) => setStats(r.data)).catch(() => {});

  const catLabel = (c) => {
    if (c.startsWith("division:")) {
      const slug = c.slice("division:".length);
      const d = divisions.find((x) => x.slug === slug);
      return d ? d.name : slug.replace(/-/g, " ");
    }
    return SIMPLE_LABELS[c] || c;
  };

  const subscribers = useMemo(() => stats?.subscribers || [], [stats]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return subscribers;
    return subscribers.filter((x) => x.email.toLowerCase().includes(s));
  }, [subscribers, q]);

  const send = async (e) => {
    e.preventDefault();
    if (divIds.length === 0) {
      toast.error("Pick at least one division to notify.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/admin/divisions/notify-multi`, { division_ids: divIds, message });
      const nd = r.data.divisions?.length || divIds.length;
      toast.success(`Sent one email to ${r.data.sent} subscriber${r.data.sent === 1 ? "" : "s"} covering ${nd} division${nd === 1 ? "" : "s"}.`);
      setMessage("");
      setDivIds([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send notification.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (sub) => {
    if (!window.confirm(`Remove ${sub.email} from notifications? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/subscribers/${sub.id}`);
      reload();
      toast.success("Subscriber removed");
    } catch {
      toast.error("Failed to remove subscriber");
    }
  };

  const setConfirmed = async (sub, confirmed) => {
    try {
      await api.put(`/admin/subscribers/${sub.id}/confirm`, { confirmed });
      reload();
      toast.success(confirmed ? `${sub.email} confirmed` : `${sub.email} set to pending`);
    } catch {
      toast.error("Failed to update subscriber");
    }
  };

  const startCatEdit = (sub) => {
    setEditCatId(sub.id);
    setCatDraft(sub.categories || []);
  };
  const cancelCatEdit = () => { setEditCatId(null); setCatDraft([]); };
  const toggleCat = (c) => setCatDraft((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  const saveCatEdit = async (sub) => {
    setSavingCat(true);
    try {
      await api.put(`/admin/subscribers/${sub.id}/categories`, { categories: catDraft });
      await reload();
      toast.success(`Updated notification options for ${sub.email}`);
      cancelCatEdit();
    } catch {
      toast.error("Failed to update notification options");
    } finally {
      setSavingCat(false);
    }
  };

  const toggleAddCat = (c) => setAddCats((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  const addSubscriber = async (e) => {
    e.preventDefault();
    if (!addEmail.trim()) { toast.error("Enter an email address."); return; }
    if (addCats.length === 0) { toast.error("Pick at least one notification option."); return; }
    setAddBusy(true);
    try {
      await api.post("/admin/subscribers", { email: addEmail.trim(), categories: addCats, send_welcome: addWelcome });
      await reload();
      toast.success(`${addEmail.trim()} added as a confirmed subscriber${addWelcome ? " — welcome email sent" : ""}`);
      setAddEmail("");
      setAddCats(["all"]);
      setAddWelcome(false);
      setShowAdd(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add subscriber");
    } finally {
      setAddBusy(false);
    }
  };

  const createSchedule = async (e) => {
    e.preventDefault();
    if (!schedDivId) {
      toast.error("Pick a division to schedule.");
      return;
    }
    const body = { division_id: schedDivId, message: schedMsg, kind: schedKind };
    if (schedKind === "once") {
      if (!runDate || !runTime) {
        toast.error("Pick a date and time.");
        return;
      }
      body.run_date = runDate;
      body.run_time = runTime;
    } else {
      body.day_of_week = Number(dayOfWeek);
      body.weekly_time = weeklyTime;
      body.end_date = endDate || null;
    }
    setSchedBusy(true);
    try {
      await api.post("/admin/scheduled-notifications", body);
      toast.success("Notification scheduled.");
      setSchedMsg("");
      setRunDate("");
      setEndDate("");
      loadSchedules();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule.");
    } finally {
      setSchedBusy(false);
    }
  };

  const toggleSchedule = async (s) => {
    try {
      await api.put(`/admin/scheduled-notifications/${s.id}/toggle`, { enabled: !s.enabled });
      loadSchedules();
    } catch {
      toast.error("Could not update schedule");
    }
  };

  const deleteSchedule = async (s) => {
    if (!window.confirm(`Delete this scheduled update for ${s.name}?`)) return;
    try {
      await api.delete(`/admin/scheduled-notifications/${s.id}`);
      loadSchedules();
      toast.success("Schedule deleted");
    } catch {
      toast.error("Could not delete schedule");
    }
  };

  const exportCsv = () => {
    const rows = [["Email", "Status", "Subscribed to", "Signed up"]];
    subscribers.forEach((s) => {
      rows.push([
        s.email,
        s.confirmed ? "Confirmed" : "Pending",
        (s.categories || []).map(catLabel).join("; "),
        s.created_at ? new Date(s.created_at).toLocaleString() : "",
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quintebca-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl space-y-8" data-testid="notifications-manager">
      {/* Stats */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-[#1E63C4]" />
          <h3 className="font-heading font-bold text-lg">Subscribers</h3>
        </div>
        {stats ? (
          <div className="flex flex-wrap gap-8">
            <div data-testid="subscribers-confirmed">
              <p className="text-3xl font-extrabold text-[#1E63C4]">{stats.confirmed}</p>
              <p className="text-sm text-[#555555]">Confirmed</p>
            </div>
            <div>
              <p className="text-3xl font-extrabold text-[#999999]">{stats.pending}</p>
              <p className="text-sm text-[#555555]">Awaiting confirmation</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#555555]">Loading…</p>
        )}
      </div>

      {/* Send a division update */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <div className="flex items-center gap-2 mb-1">
          <Megaphone className="h-5 w-5 text-[#1E63C4]" />
          <h3 className="font-heading font-bold text-lg">Send a division update</h3>
        </div>
        <p className="text-sm text-[#555555] mb-5">Select one or more divisions. Everyone subscribed to any selected division (and to &quot;All updates&quot;) gets a <strong>single combined email</strong> — no duplicates. Use this when you post new standings, schedules, or weekly highlights.</p>
        <form onSubmit={send} className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-[#333333]">Divisions ({divIds.length} selected)</label>
              <button type="button" data-testid="notify-select-all" onClick={() => setDivIds(divIds.length === divisions.length ? [] : divisions.map((d) => d.id))} className="text-xs font-semibold text-[#1E63C4] hover:underline">
                {divIds.length === divisions.length ? "Clear all" : "Select all"}
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="notify-division-list">
              {divisions.map((d) => (
                <label key={d.id} data-testid={`notify-div-${d.id}`} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer ${divIds.includes(d.id) ? "border-[#1E63C4] bg-blue-50" : "border-[#E5E7EB] hover:bg-[#F9FAF8]"}`}>
                  <input type="checkbox" checked={divIds.includes(d.id)} onChange={() => toggleDiv(d.id)} className="h-4 w-4 accent-[#1E63C4]" />
                  <span className="truncate">{d.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#333333] mb-1">Message (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              data-testid="notify-division-message"
              placeholder="e.g. Week 6 standings are posted — check out the new leaders!"
              className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4] resize-y"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="notify-division-send"
            className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            {busy ? "Sending…" : "Send notification"}
          </button>
        </form>
      </div>

      {/* Subscriber list */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <button
            onClick={() => setSubsOpen((o) => !o)}
            data-testid="subscriber-list-toggle"
            aria-expanded={subsOpen}
            className="inline-flex items-center gap-2 font-heading font-bold text-lg hover:text-[#1E63C4] transition-colors"
          >
            <ChevronDown className={`h-5 w-5 text-[#999] transition-transform ${subsOpen ? "" : "-rotate-90"}`} />
            Subscriber list
            <span className="text-sm font-normal text-[#999]">({subscribers.length})</span>
          </button>
          <button
            onClick={exportCsv}
            disabled={subscribers.length === 0}
            data-testid="subscribers-export-csv"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E63C4] hover:underline disabled:opacity-40 disabled:no-underline"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>

        <div className="mb-4">
          <button
            onClick={() => setShowAdd((v) => !v)}
            data-testid="subscriber-add-toggle"
            className="inline-flex items-center gap-1.5 text-sm font-semibold border border-[#1E63C4] text-[#1E63C4] rounded-md px-3 py-2 hover:bg-blue-50"
          >
            <UserPlus className="h-4 w-4" /> {showAdd ? "Cancel" : "Add subscriber manually"}
          </button>
          {showAdd && (
            <form onSubmit={addSubscriber} className="mt-3 rounded-md border border-[#E5E7EB] bg-[#F9FAF8] p-4 space-y-3" data-testid="subscriber-add-form">
              <div>
                <label className="block text-xs font-semibold text-[#555] mb-1">Email</label>
                <input
                  type="email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="name@example.com"
                  data-testid="subscriber-add-email"
                  className="w-full max-w-sm rounded-md border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#555] mb-2">Notification options</label>
                <div className="flex flex-wrap gap-1.5">
                  {SIMPLE_CATS.map((c) => (
                    <label key={c} data-testid={`sub-add-cat-${c}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs cursor-pointer ${addCats.includes(c) ? "border-[#1E63C4] bg-[#1E63C4]/10 text-[#1E63C4] font-semibold" : "border-[#E5E7EB] text-[#555] hover:bg-white"}`}>
                      <input type="checkbox" checked={addCats.includes(c)} onChange={() => toggleAddCat(c)} className="h-3.5 w-3.5 accent-[#1E63C4]" />
                      {SIMPLE_LABELS[c]}
                    </label>
                  ))}
                  {divisions.map((d) => {
                    const c = `division:${d.slug}`;
                    return (
                      <label key={c} data-testid={`sub-add-cat-${d.slug}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs cursor-pointer ${addCats.includes(c) ? "border-[#1E63C4] bg-[#1E63C4]/10 text-[#1E63C4] font-semibold" : "border-[#E5E7EB] text-[#555] hover:bg-white"}`}>
                        <input type="checkbox" checked={addCats.includes(c)} onChange={() => toggleAddCat(c)} className="h-3.5 w-3.5 accent-[#1E63C4]" />
                        {d.name}
                      </label>
                    );
                  })}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="subscriber-add-welcome-label">
                <input type="checkbox" checked={addWelcome} onChange={(e) => setAddWelcome(e.target.checked)} data-testid="subscriber-add-welcome" className="h-4 w-4 accent-[#1E63C4]" />
                <span className="text-[#333]">Send welcome email</span>
              </label>
              <button type="submit" disabled={addBusy} data-testid="subscriber-add-submit" className="inline-flex items-center gap-1.5 text-sm bg-[#1E63C4] text-white rounded-md px-4 py-2 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
                {addBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add subscriber
              </button>
              <p className="text-xs text-[#999]">Manually added subscribers are marked <strong>confirmed</strong> — no confirmation email is sent{addWelcome ? ", but a welcome email will be sent" : ""}.</p>
            </form>
          )}
        </div>

        {subsOpen && (<>
        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#999999]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email…"
            data-testid="subscribers-search"
            className="w-full rounded-md border border-[#E5E7EB] pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-10 text-[#555555]" data-testid="subscribers-empty">
            <Users className="h-9 w-9 mx-auto mb-2 text-[#CBD5E1]" />
            <p>{subscribers.length === 0 ? "No subscribers yet. Sign-ups from the Notify page will appear here." : "No subscribers match your search."}</p>
          </div>
        ) : (
          <div className="divide-y divide-[#F0F0F0]">
            {filtered.map((s) => (
              <div key={s.id} data-testid={`subscriber-row-${s.id}`} className="flex items-start justify-between gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <a href={`mailto:${s.email}`} className="font-medium text-[#111111] hover:text-[#1E63C4] truncate">{s.email}</a>
                    {s.confirmed ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold shrink-0"><CheckCircle2 className="h-3.5 w-3.5" /> Confirmed</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-semibold shrink-0"><Clock className="h-3.5 w-3.5" /> Pending</span>
                    )}
                  </div>
                  {editCatId === s.id ? (
                    <div className="mt-2 rounded-md border border-[#E5E7EB] bg-[#F9FAF8] p-3" data-testid={`subscriber-cat-edit-${s.id}`}>
                      <p className="text-xs font-semibold text-[#555] mb-2">Notification options</p>
                      <div className="flex flex-wrap gap-1.5">
                        {SIMPLE_CATS.map((c) => (
                          <label key={c} data-testid={`sub-cat-${s.id}-${c}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs cursor-pointer ${catDraft.includes(c) ? "border-[#1E63C4] bg-[#1E63C4]/10 text-[#1E63C4] font-semibold" : "border-[#E5E7EB] text-[#555] hover:bg-white"}`}>
                            <input type="checkbox" checked={catDraft.includes(c)} onChange={() => toggleCat(c)} className="h-3.5 w-3.5 accent-[#1E63C4]" />
                            {SIMPLE_LABELS[c]}
                          </label>
                        ))}
                        {divisions.map((d) => {
                          const c = `division:${d.slug}`;
                          return (
                            <label key={c} data-testid={`sub-cat-${s.id}-${d.slug}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs cursor-pointer ${catDraft.includes(c) ? "border-[#1E63C4] bg-[#1E63C4]/10 text-[#1E63C4] font-semibold" : "border-[#E5E7EB] text-[#555] hover:bg-white"}`}>
                              <input type="checkbox" checked={catDraft.includes(c)} onChange={() => toggleCat(c)} className="h-3.5 w-3.5 accent-[#1E63C4]" />
                              {d.name}
                            </label>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <button onClick={() => saveCatEdit(s)} disabled={savingCat} data-testid={`sub-cat-save-${s.id}`} className="inline-flex items-center gap-1.5 text-xs bg-[#1E63C4] text-white rounded-md px-3 py-1.5 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
                          {savingCat ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                        </button>
                        <button onClick={cancelCatEdit} data-testid={`sub-cat-cancel-${s.id}`} className="inline-flex items-center gap-1.5 text-xs border border-[#E5E7EB] text-[#555] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]"><X className="h-3.5 w-3.5" /> Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {(s.categories || []).length ? (s.categories || []).map((c) => (
                        <span key={c} className="inline-block rounded-full bg-[#1E63C4]/10 text-[#1E63C4] text-xs px-2 py-0.5">{catLabel(c)}</span>
                      )) : <span className="text-xs text-[#999]">No options selected</span>}
                      <button onClick={() => startCatEdit(s)} data-testid={`subscriber-edit-cats-${s.id}`} className="inline-flex items-center gap-1 text-xs text-[#1E63C4] font-medium hover:underline ml-1"><Pencil className="h-3 w-3" /> Edit options</button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {s.confirmed ? (
                    <button
                      onClick={() => setConfirmed(s, false)}
                      data-testid={`subscriber-pending-${s.id}`}
                      className="inline-flex items-center gap-1 text-xs border border-[#E5E7EB] text-[#555555] rounded-md px-2.5 py-1.5 hover:bg-[#F9FAF8]"
                    >
                      <Clock className="h-3.5 w-3.5" /> Set pending
                    </button>
                  ) : (
                    <button
                      onClick={() => setConfirmed(s, true)}
                      data-testid={`subscriber-confirm-${s.id}`}
                      className="inline-flex items-center gap-1 text-xs bg-[#1E63C4] text-white rounded-md px-2.5 py-1.5 font-semibold hover:bg-[#174e9c]"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> Confirm
                    </button>
                  )}
                  <button
                    onClick={() => remove(s)}
                    data-testid={`subscriber-delete-${s.id}`}
                    aria-label={`Remove ${s.email}`}
                    className="text-[#999999] hover:text-[#E11D2A] p-1"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </>)}
      </div>

      {/* Schedule a division update */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6" data-testid="schedule-notifications">
        <div className="flex items-center gap-2 mb-1">
          <CalendarClock className="h-5 w-5 text-[#1E63C4]" />
          <h3 className="font-heading font-bold text-lg">Schedule a division update</h3>
        </div>
        <p className="text-sm text-[#555555] mb-5">Set up a one-off send at a future date/time, or a weekly recurring send. Times are <strong>Eastern (ET)</strong>. Emails go to that division&apos;s subscribers (and &quot;All updates&quot;).</p>

        <form onSubmit={createSchedule} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#333333] mb-1">Division</label>
              <select
                value={schedDivId}
                onChange={(e) => setSchedDivId(e.target.value)}
                data-testid="sched-division-select"
                className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
              >
                <option value="">Choose a division…</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#333333] mb-1">Schedule type</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setSchedKind("once")} data-testid="sched-kind-once" className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2.5 text-sm font-semibold border ${schedKind === "once" ? "bg-[#1E63C4] text-white border-[#1E63C4]" : "border-[#E5E7EB] text-[#555555] hover:bg-[#F9FAF8]"}`}>
                  <CalendarDays className="h-4 w-4" /> One-off
                </button>
                <button type="button" onClick={() => setSchedKind("weekly")} data-testid="sched-kind-weekly" className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2.5 text-sm font-semibold border ${schedKind === "weekly" ? "bg-[#1E63C4] text-white border-[#1E63C4]" : "border-[#E5E7EB] text-[#555555] hover:bg-[#F9FAF8]"}`}>
                  <Repeat className="h-4 w-4" /> Weekly
                </button>
              </div>
            </div>
          </div>

          {schedKind === "once" ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#333333] mb-1">Date</label>
                <input type="date" value={runDate} onChange={(e) => setRunDate(e.target.value)} data-testid="sched-run-date" className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333333] mb-1">Time (ET)</label>
                <input type="time" value={runTime} onChange={(e) => setRunTime(e.target.value)} data-testid="sched-run-time" className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]" />
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#333333] mb-1">Day of week</label>
                <select value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)} data-testid="sched-day-select" className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]">
                  {DOW.map((d, i) => (<option key={d} value={i}>{d}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333333] mb-1">Time (ET)</label>
                <input type="time" value={weeklyTime} onChange={(e) => setWeeklyTime(e.target.value)} data-testid="sched-weekly-time" className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333333] mb-1">End date <span className="text-[#999999] font-normal">(optional)</span></label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="sched-end-date" className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]" />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[#333333] mb-1">Message (optional)</label>
            <textarea
              value={schedMsg}
              onChange={(e) => setSchedMsg(e.target.value)}
              rows={2}
              data-testid="sched-message"
              placeholder="e.g. Weekly standings are posted — see who's on top!"
              className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4] resize-y"
            />
          </div>
          <button type="submit" disabled={schedBusy} data-testid="sched-create-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
            {schedBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CalendarClock className="h-5 w-5" />}
            {schedBusy ? "Scheduling…" : "Schedule it"}
          </button>
        </form>

        {schedules.length > 0 && (
          <div className="mt-6 border-t border-[#F0F0F0] pt-5 space-y-3" data-testid="schedule-list">
            <h4 className="font-semibold text-sm text-[#333333]">Scheduled updates</h4>
            {schedules.map((s) => (
              <div key={s.id} data-testid={`schedule-row-${s.id}`} className="flex items-start justify-between gap-3 rounded-lg border border-[#E5E7EB] p-3.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#111111]">{s.name}</span>
                    {s.kind === "weekly" ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E63C4] bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5"><Repeat className="h-3 w-3" /> Weekly · {DOW[s.day_of_week] || "?"} {s.weekly_time}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#555] bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5"><CalendarDays className="h-3 w-3" /> One-off</span>
                    )}
                    {!s.enabled && <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Paused</span>}
                  </div>
                  {s.message && <p className="text-sm text-[#555555] mt-1 line-clamp-2">{s.message}</p>}
                  <p className="text-xs text-[#999999] mt-1">
                    {s.enabled ? <>Next send: <span className="font-medium text-[#555]">{fmtEastern(s.next_run_at)}</span></> : "Paused"}
                    {s.kind === "weekly" && s.end_date && <> · ends {s.end_date}</>}
                    {s.last_run_at && <> · last sent {fmtEastern(s.last_run_at)}</>}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => toggleSchedule(s)} data-testid={`schedule-toggle-${s.id}`} title={s.enabled ? "Pause" : "Resume"} className={`inline-flex items-center gap-1 text-xs border rounded-md px-2.5 py-1.5 ${s.enabled ? "border-[#E5E7EB] text-[#555555] hover:bg-[#F9FAF8]" : "border-[#1E63C4] text-[#1E63C4] hover:bg-blue-50"}`}>
                    <Power className="h-3.5 w-3.5" /> {s.enabled ? "Pause" : "Resume"}
                  </button>
                  <button onClick={() => deleteSchedule(s)} data-testid={`schedule-delete-${s.id}`} aria-label="Delete schedule" className="text-[#999999] hover:text-[#E11D2A] p-1.5"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-blue-50/60 border border-[#62A8E5]/40 rounded-lg p-5 text-sm text-[#333333] flex gap-3">
        <Bell className="h-5 w-5 text-[#1E63C4] shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold mb-1">Automatic notifications</p>
          <p className="leading-relaxed">New <strong>tournaments</strong>, <strong>promos</strong> and <strong>merch</strong> you add are emailed to their subscribers automatically. <strong>Live stream</strong> subscribers are alerted when the channel goes live. <strong>Division</strong> updates can be sent now or scheduled above.</p>
        </div>
      </div>
    </div>
  );
}
